import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { RecipientChange } from '@citibox-panel/api-client';

// The change-recipient WRITE («Cambiar titular»), the panel's tenth and its eighth over a
// «Entrega». The write fn is injected (DI, MSW-testable). retry: false — a write that moves the
// ownership of a parcel must never be silently re-sent, and this one is not even idempotent in
// intention: a second attempt carries an `expectedCurrentUserId` its own first attempt has already
// invalidated, so the retry would fail with a conflict about a change the agent did make.
//
// **The invalidations are modelled on `useUpdateUser`, not on this card's own siblings**, and the
// difference is the point. The six actions beside it move a delivery's STATE, so they refresh the
// three «buzón»-keyed reads with it. This one moves a NAME on a row: the parcel has not moved, the
// box is not freed, the opening codes are untouched. What goes stale is every screen that PRINTS
// the titular — which is exactly the set `useUpdateUser` refreshes when a «Usuario» is renamed,
// `tracking-search` included. Invalidating `locker-log`, `box-status` or `lock-codes` here would be
// three requests that could not come back with anything new.
//
// The detail goes by EXACT key (this hook knows the id) and the three lists by PREFIX, since it
// knows neither the userId nor which building's tab is open. Fire-and-forget: do NOT return the
// promise, or TanStack awaits the refetch before the mutation settles and the caller loses the
// refetch skeleton it drives off the query's own fetching state.
export function useChangeRecipient(
  id: string,
  changeDeliveryRecipient: (id: string, change: RecipientChange) => Promise<void>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (change: RecipientChange) => changeDeliveryRecipient(id, change),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['delivery-detail', id] });
      void queryClient.invalidateQueries({ queryKey: ['user-deliveries'] });
      // …and the same rows reached from a building's tab: same list, other entry point.
      void queryClient.invalidateQueries({ queryKey: ['location-deliveries'] });
      // The tracking answer carries `recipient: {id, name, phone}` per row, so a reassignment
      // leaves it naming the previous titular. The card's siblings do not touch this one because a
      // state change does not alter who a parcel is for; this write does nothing else.
      void queryClient.invalidateQueries({ queryKey: ['tracking-search'] });
    },
  });
}
